import { useEffect, useState, useMemo } from "preact/hooks";

import { Link } from "react-router-dom";
import { Text, Localizer } from "preact-i18n";
import { useQuery, useMutation } from "@apollo/client";

import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Select,
  MenuItem,
  Switch,
  debounce,
  TextField,
  InputAdornment,
  FormGroup,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import { Security, ArrowRightAlt, Dashboard, Close } from "@mui/icons-material";

import DebouncedProgress from "../DebouncedProgress/DebouncedProgress";

import {
  UserSettingsQuery,
  ObfuscatedAliasDemoQuery,
  UpdateSettingsMutation,
} from "./Settings.graphql";

export function useAliasDemoQuery(aliasStyle) {
  const { data } = useQuery(ObfuscatedAliasDemoQuery, {
    variables: {
      aliasStyle,
    },
    fetchPolicy: "no-cache",
  });

  return data?.obfuscatedAliasDemo?.alias || "";
}

function useUpdateSettingMutation() {
  return useMutation(UpdateSettingsMutation, {
    refetchQueries: [{ query: UserSettingsQuery }],
  });
}

export function useUpdateSetting(setting, current) {
  const [updateSettings, { loading }] = useUpdateSettingMutation();

  return [
    (newValue) => {
      if (current === newValue) return;
      updateSettings({
        variables: {
          settings: {
            [setting]: newValue,
          },
        },
      });
    },
    { loading },
  ];
}

export function useSetting(setting) {
  const { data, loading } = useQuery(UserSettingsQuery, {
    fetchPolicy: "cache-and-network",
  });
  const current = data?.userSettings?.[setting];
  const [updateSetting, { loading: mutationLoading }] = useUpdateSetting(
    setting,
    current
  );

  const [state, setState] = useState(current);

  useEffect(() => {
    setState(current);
  }, [current]);

  return [
    { loading: loading || mutationLoading, setting: state },
    (newValue) => {
      setState(newValue);
      updateSetting(newValue);
    },
  ];
}

const SelectedArrow = ({ aliasStyle, style }) => {
  return aliasStyle === style ? (
    <div className="px-2">
      <ArrowRightAlt />
    </div>
  ) : (
    <div class="w-10" />
  );
};

const AliasStyleSetting = ({ settings: { aliasStyle } }) => {
  const humanDemo = useAliasDemoQuery("HUMAN");
  const randomDemo = useAliasDemoQuery("RANDOM");

  const [updateSettings] = useMutation(UpdateSettingsMutation, {
    refetchQueries: [{ query: UserSettingsQuery }],
  });

  const onClick = (style) => () => {
    if (aliasStyle === style) return;

    updateSettings({
      variables: {
        settings: {
          aliasStyle: style,
        },
      },
    });
  };

  return (
    <Accordion>
      <AccordionSummary>
        <ListItem>
          <ListItemIcon>
            <Security />
          </ListItemIcon>
          <Localizer>
            <ListItemText primary={<Text id="Settings.ObfuscationStyle" />} />
          </Localizer>
        </ListItem>
      </AccordionSummary>
      <AccordionDetails>
        <div>
          <p>
            <Text id="Settings.ObfuscationStyleDescription" />
          </p>
          <br />
          <div class="flex flex-col w-full cursor-pointer">
            <div onClick={onClick("HUMAN")} class="flex items-center">
              <SelectedArrow aliasStyle={aliasStyle} style="HUMAN" />
              <Localizer>
                <Tooltip
                  title={
                    aliasStyle === "HUMAN" ? (
                      <Text id="Settings.usingHuman" />
                    ) : (
                      <Text id="Settings.changeToHuman" />
                    )
                  }
                >
                  <span>{humanDemo}</span>
                </Tooltip>
              </Localizer>
            </div>
            <div onClick={onClick("RANDOM")} class="flex items-center">
              <SelectedArrow aliasStyle={aliasStyle} style="RANDOM" />

              <Localizer>
                <Tooltip
                  title={
                    aliasStyle === "RANDOM" ? (
                      <Text id="Settings.usingRandom" />
                    ) : (
                      <Text id="Settings.changeToRandom" />
                    )
                  }
                >
                  <span>{randomDemo}</span>
                </Tooltip>
              </Localizer>
            </div>
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

const NewMailEditorMode = ({ settings }) => {
  const [update] = useUpdateSetting(
    "newMailEditorMode",
    settings?.newMailEditorMode
  );

  return (
    <div className="prose">
      <h4 className="text-lg">
        <Text id="Settings.newMailEditorMode.heading" />
      </h4>
      <p className="my-1">
        <Text id="Settings.newMailEditorMode.description" />
      </p>
      <div className="mt-2">
        <Localizer>
          <Select
            value={settings?.newMailEditorMode}
            label={<Text id="Settings.newMailEditorMode.selectLabel" />}
            onChange={(event) => {
              const selected = event.target.value;

              update(selected);
            }}
            fullWidth
          >
            <MenuItem value={"PANEL"}>
              <Text id="Settings.newMailEditorMode.modes.panel" />
            </MenuItem>
            <MenuItem value={"POPOUT"}>
              <Text id="Settings.newMailEditorMode.modes.popout" />
            </MenuItem>
            <MenuItem value={null}>
              <Text id="Settings.newMailEditorMode.modes.default" />
            </MenuItem>
          </Select>
        </Localizer>
      </div>
    </div>
  );
};

const NumberOfMessagesToFetch = ({ settings }) => {
  const [update, { loading }] = useUpdateSetting(
    "numberOfMessagesToFetch",
    settings?.numberOfMessagesToFetch
  );

  const onChange = useMemo(() =>
    debounce((event) => update(parseInt(event.target.value, 10)), 400)
  );

  return (
    <div className="prose">
      <h4 className="text-lg">
        <Text id="Settings.numberOfMessagesToFetch.heading" />
      </h4>
      <p className="my-1">
        <Text id="Settings.numberOfMessagesToFetch.description" />
      </p>
      <div className="mt-2">
        <Localizer>
          <TextField
            value={settings?.numberOfMessagesToFetch}
            label={<Text id="Settings.numberOfMessagesToFetch.label" />}
            type="number"
            onChange={onChange}
            fullWidth
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              min: 0,
              max: 249,
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <DebouncedProgress color="inherit" value={loading} />
                </InputAdornment>
              ),
            }}
          />
        </Localizer>
      </div>
    </div>
  );
};

const ShowCounters = ({ settings }) => {
  const [update, { loading }] = useUpdateSetting(
    "showCounters",
    settings?.showCounters
  );

  return (
    <div className="prose">
      <h4 className="text-lg">
        <Text id="Settings.showCounters.heading" />
      </h4>
      <p className="my-1">
        <Text id="Settings.showCounters.description" />
      </p>
      <div className="mt-2">
        <Localizer>
          <FormControlLabel
            label={<Text id="Settings.showCounters.label" />}
            control={
              <Switch
                checked={settings?.showCounters}
                onChange={(event) => update(event.target.checked)}
              />
            }
          />
        </Localizer>
        {/* <Localizer>
          <Tooltip title={<Text id="Settings.showCounters.clear" />}>
            <IconButton onClick={() => update(null)}>
              <Close />
            </IconButton>
          </Tooltip>
        </Localizer> */}
      </div>
    </div>
  );
};

const SpecialUnreadEmphasis = ({ settings }) => {
  const [update, { loading }] = useUpdateSetting(
    "specialUnreadEmphasis",
    settings?.specialUnreadEmphasis
  );

  return (
    <div className="prose">
      <h4 className="text-lg">
        <Text id="Settings.specialUnreadEmphasis.heading" />
      </h4>
      <p className="my-1">
        <Text id="Settings.specialUnreadEmphasis.description" />
      </p>
      <div className="mt-2">
        <Localizer>
          <FormControlLabel
            label={<Text id="Settings.specialUnreadEmphasis.label" />}
            control={
              <Switch
                checked={settings?.specialUnreadEmphasis}
                onChange={(event) => update(event.target.checked)}
              />
            }
          />
        </Localizer>
        {/* <Localizer>
          <Tooltip title={<Text id="Settings.specialUnreadEmphasis.clear" />}>
            <IconButton onClick={() => update(null)}>
              <Close />
            </IconButton>
          </Tooltip>
        </Localizer> */}
      </div>
    </div>
  );
};

const UISettings = ({ settings }) => {
  return (
    <Accordion>
      <AccordionSummary>
        <ListItem>
          <ListItemIcon>
            <Dashboard />
          </ListItemIcon>
          <Localizer>
            <ListItemText primary={"UI"} />
          </Localizer>
        </ListItem>
      </AccordionSummary>
      <AccordionDetails>
        <div>
          <NewMailEditorMode settings={settings} />

          <br />

          <NumberOfMessagesToFetch settings={settings} />

          <br />

          <ShowCounters settings={settings} />

          <SpecialUnreadEmphasis settings={settings} />
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

const Settings = () => {
  const { data, loading } = useQuery(UserSettingsQuery);

  if (loading) return <Text id="loading" />;

  return (
    <List dense class="h-full w-full">
      <Link
        to="/dashboard/settings/application-passwords"
        className="text-blue-400 underline"
      >
        <Accordion>
          <AccordionSummary>
            <ListItem>
              <ListItemIcon>
                <Security />
              </ListItemIcon>
              <Localizer>
                <ListItemText
                  primary={<Text id="Settings.ApplicationPasswords" />}
                />
              </Localizer>
            </ListItem>
          </AccordionSummary>
        </Accordion>
      </Link>

      <AliasStyleSetting settings={data?.userSettings} />

      <UISettings settings={data?.userSettings} />
    </List>
  );
};

export default Settings;
